import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/danger/ConfirmDialog';
import { ActionGroup, DangerButton } from '@/components/danger/DangerButton';
import { StateChip } from '@/components/primitives/StateChip';
import { ErrorState } from '@/components/states/ErrorState';
import { SkeletonTable } from '@/components/states/Skeleton';
import {
  useAssessWeekOperation,
  useRunWeekOperation,
  useWeeks,
  WEEK_OPERATION_LABELS,
  type WeekAssessment,
  type WeekOperation,
  type WeekRow,
} from '@/features/leagues/weeks';
import { ApiError } from '@/lib/api/client';

export const Route = createFileRoute('/leagues/$leagueId/weeks')({
  component: WeeksScreen,
});

const OPERATIONS: WeekOperation[] = [
  'recompute',
  'advance_lifecycle',
  'reenqueue_scoring',
  'clear_stat_cache',
];

function WeeksScreen() {
  const { leagueId } = Route.useParams();
  const { data, isPending, isError, error, refetch } = useWeeks(leagueId);

  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [pending, setPending] = useState<{
    week: number;
    operation: WeekOperation;
    assessment: WeekAssessment;
  } | null>(null);

  const assess = useAssessWeekOperation(leagueId);
  const run = useRunWeekOperation(leagueId);

  async function begin(week: number, operation: WeekOperation) {
    try {
      const assessment = await assess.mutateAsync({ week, operation });
      setPending({ week, operation, assessment });
    } catch {
      toast.error('Could not assess this operation', {
        description: 'No confirmation was shown, so nothing was altered.',
      });
    }
  }

  function confirm(reason: string) {
    if (!pending) return;
    run.mutate(
      { week: pending.week, operation: pending.operation, reason },
      {
        onSuccess: (result) => {
          setPending(null);
          void refetch();
          toast.success(`${WEEK_OPERATION_LABELS[pending.operation]} started`, {
            description:
              result.enqueuedJobs.length > 0
                ? `Queued: ${result.enqueuedJobs.join(', ')}`
                : undefined,
          });
        },
      },
    );
  }

  if (isError) {
    return <ErrorState error={error} subject="weeks" onRetry={() => void refetch()} />;
  }
  if (isPending) {
    return <SkeletonTable rows={14} columns={6} />;
  }

  const active = selectedWeek ?? data.weeks.find((w) => w.status === 'active')?.week ?? null;
  const runError = run.error;

  return (
    <div className="flex flex-col gap-3.5">
      <section className="border-bd bg-card overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max border-collapse text-left">
            <thead>
              <tr className="border-bd bg-elev border-b">
                {['Wk', 'Status', 'Lock time', 'Matchups', 'Final', 'Note'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="font-head text-tx3 px-3.5 py-2 text-[10.5px] font-semibold uppercase"
                    style={{ letterSpacing: '0.14em' }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.weeks.map((week) => (
                <WeekRowView
                  key={week.week ?? 0}
                  week={week}
                  isSelected={week.week === active}
                  onSelect={() => setSelectedWeek(week.week)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {active !== null && (
        <>
          <ActionGroup tier={1}>
            <DangerButton tier={1} onClick={() => void refetch()}>
              Refresh
            </DangerButton>
          </ActionGroup>

          <ActionGroup tier={3} hint="rewrites published results">
            {OPERATIONS.map((operation) => (
              <DangerButton
                key={operation}
                tier={3}
                loading={assess.isPending}
                onClick={() => void begin(active, operation)}
              >
                {WEEK_OPERATION_LABELS[operation]} · W{active}
              </DangerButton>
            ))}
          </ActionGroup>
        </>
      )}

      {pending && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !run.isPending) {
              setPending(null);
              run.reset();
            }
          }}
          tier={pending.assessment.tier}
          title={`${WEEK_OPERATION_LABELS[pending.operation]} · week ${String(pending.week)}`}
          summary={
            pending.assessment.supportedUpstream ? (
              <>This runs against week {pending.week} and cannot be undone from the console.</>
            ) : (
              <>
                <strong className="text-warn">Not available yet.</strong> ZazzBackend has no
                admin-callable endpoint for this operation, so it cannot be run. The
                consequences below are what it <em>would</em> do.
              </>
            )
          }
          consequences={pending.assessment.consequences}
          typeToConfirmValue={`week ${String(pending.week)}`}
          typeToConfirmLabel="week"
          confirmLabel={
            pending.assessment.supportedUpstream ? 'Run it' : 'Unavailable upstream'
          }
          isPending={run.isPending}
          isIndeterminate={runError instanceof ApiError && runError.isIndeterminate}
          errorMessage={runError instanceof Error ? runError.message : null}
          onConfirm={confirm}
        />
      )}
    </div>
  );
}

function WeekRowView({
  week,
  isSelected,
  onSelect,
}: {
  week: WeekRow;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <tr
      className="border-bd/60 hover:bg-elev/60 cursor-pointer border-b last:border-b-0"
      style={isSelected ? { background: 'var(--elev)' } : undefined}
      onClick={onSelect}
    >
      <td className="mesh-mono text-tx px-3.5 py-2.5 text-[12.5px]">{week.week ?? '—'}</td>
      <td className="px-3.5 py-2.5">
        {week.status ? <StateChip state={week.status} size="sm" /> : '—'}
      </td>
      <td className="mesh-mono text-tx3 px-3.5 py-2.5 text-[11.5px]">
        {week.lockAt ? week.lockAt.replace('T', ' ').slice(0, 16) : '—'}
      </td>
      <td className="mesh-mono text-tx2 px-3.5 py-2.5 text-[12px]">
        {week.matchups}
        {week.scoredMatchups > 0 && (
          <span className="text-tx3"> · {week.scoredMatchups} scored</span>
        )}
      </td>
      <td className="px-3.5 py-2.5 text-[12px]">
        {week.isFinal ? (
          <span className="text-ok">yes</span>
        ) : (
          <span className="text-tx3">no</span>
        )}
      </td>
      <td className="px-3.5 py-2.5 text-[12px]">
        {week.lastLifecycleError ? (
          <span className="mesh-mono text-err-soft text-[11px]">{week.lastLifecycleError}</span>
        ) : (
          <span className="text-tx3">{week.lastLifecycleStep ?? '—'}</span>
        )}
      </td>
    </tr>
  );
}
